import { useCallback, useEffect, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import { CareShell } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { useFileUpload } from "../../hooks/useFileUpload";
import useSiteProfile from "../../hooks/useSiteProfile";
import { careService } from "../../services/careService";
import { getCroppedImageFile } from "../../utils/imageCropUtils";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const defaultProfile = {
  name: "",
  themePhrase: "",
  phone: "",
  email: "",
  location: "",
  customerInquiryEmails: [],
  customerInquiryPhones: [],
  logo: "",
  favicon: "",
  mainColor: "#2385db",
  secondaryColor: "#f8c10b",
  siteThemeColor: "#04152f",
  secondarySiteThemeColor: "#0f4c81",
};

const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";

const listToText = (value) => (Array.isArray(value) ? value.join(", ") : "");

const textToList = (value) =>
  String(value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const colorInputValue = (value) =>
  /^#[0-9a-f]{6}$/i.test(String(value || "")) ? value : "#2385db";

const isUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const fullAddressFromPlace = (details, fallback = "") => {
  if (!details) return fallback;
  const fields = addressFieldsFromPlaceDetails(details);
  const stateZip = [fields.state, fields.zipCode].filter(Boolean).join(" ");
  const cityStateZip = [fields.city, stateZip].filter(Boolean).join(", ");
  const fullAddress = [fields.addressLine1, cityStateZip, fields.country].filter(Boolean).join(", ");
  return fullAddress || details.formatted_address || fallback;
};

const normalizeProfile = (profile = {}) => ({
  ...defaultProfile,
  ...profile,
  customerInquiryEmails: Array.isArray(profile.customerInquiryEmails) ? profile.customerInquiryEmails : [],
  customerInquiryPhones: Array.isArray(profile.customerInquiryPhones) ? profile.customerInquiryPhones : [],
});

function SiteImageCropModal({
  aspect = 3 / 1,
  cropAreaLabel = "image",
  fileName,
  imageSrc,
  outputType = "NAVBAR_LOGO",
  onCancel,
  onConfirm,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setPixelCrop(pixels);
  }, []);

  const applyCrop = async () => {
    if (!pixelCrop) return;
    setProcessing(true);
    try {
      const croppedFile = await getCroppedImageFile(imageSrc, pixelCrop, fileName, outputType);
      onConfirm(croppedFile);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="site-logo-crop-modal" role="dialog" aria-modal="true" aria-label={`Crop ${cropAreaLabel}`}>
      <div className="site-logo-crop-panel">
        <div className="site-logo-crop-head">
          <div>
            <strong>Crop {cropAreaLabel}</strong>
            <span>Fit the {cropAreaLabel} to the right display shape.</span>
          </div>
          <button className="icon-btn" type="button" onClick={onCancel} aria-label="Close">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="site-logo-crop-stage">
          <Cropper
            aspect={aspect}
            crop={crop}
            image={imageSrc}
            maxZoom={4}
            minZoom={1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            zoom={zoom}
            style={{
              cropAreaStyle: {
                border: "2px solid #2385db",
                boxShadow: "0 0 0 9999px rgba(4, 21, 47, 0.58)",
              },
            }}
          />
        </div>
        <div className="site-logo-crop-controls">
          <i className="ti ti-zoom-out" />
          <input
            aria-label={`${cropAreaLabel} zoom`}
            max={4}
            min={1}
            onChange={(event) => setZoom(Number(event.target.value))}
            step={0.05}
            type="range"
            value={zoom}
          />
          <i className="ti ti-zoom-in" />
        </div>
        <div className="site-logo-crop-actions">
          <button className="mini-btn" type="button" onClick={onCancel}>Cancel</button>
          <button className="btn primary" type="button" onClick={applyCrop} disabled={!pixelCrop || processing}>
            <i className={`ti ${processing ? "ti-loader-2" : "ti-crop"}`} /> {processing ? "Cropping..." : `Use cropped ${cropAreaLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SiteSettingsPage() {
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const { uploadFile, getFileSignedUrl, uploading: fileUploading } = useFileUpload();
  const { refreshSiteProfile } = useSiteProfile();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const actorId = currentUserId(user);
  const workspaceName = selectedWorkspace?.name || selectedWorkspace?.workspaceName || "Workspace";
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    appConfig?.google?.mapsApiKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const [form, setForm] = useState(defaultProfile);
  const [inquiryEmails, setInquiryEmails] = useState("");
  const [inquiryPhones, setInquiryPhones] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoStatus, setLogoStatus] = useState("");
  const [logoCrop, setLogoCrop] = useState(null);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState("");
  const [faviconStatus, setFaviconStatus] = useState("");
  const [faviconCrop, setFaviconCrop] = useState(null);

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      if (!workspaceListReady) return;
      if (!selectedWorkspaceId) {
        setForm(defaultProfile);
        setInquiryEmails("");
        setInquiryPhones("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setMessage("");
      try {
        const profile = normalizeProfile(await careService.siteProfile.get(workspaceParams, actorId));
        if (!active) return;
        setForm(profile);
        setInquiryEmails(listToText(profile.customerInquiryEmails));
        setInquiryPhones(listToText(profile.customerInquiryPhones));
      } catch (nextError) {
        if (!active) return;
        setForm(defaultProfile);
        setInquiryEmails("");
        setInquiryPhones("");
        setError(nextError?.response?.data?.message || nextError?.message || "Unable to load site settings.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      active = false;
    };
  }, [actorId, selectedWorkspaceId, workspaceListReady, workspaceParams]);

  useEffect(() => {
    let active = true;
    const logo = String(form.logo || "").trim();
    const logoUrl = String(form.logoUrl || "").trim();
    setLogoStatus("");

    if (!logo) {
      setLogoPreviewUrl("");
      return () => {
        active = false;
      };
    }

    if (isUrl(logoUrl)) {
      setLogoPreviewUrl(logoUrl);
      return () => {
        active = false;
      };
    }

    if (isUrl(logo)) {
      setLogoPreviewUrl(logo);
      return () => {
        active = false;
      };
    }

    getFileSignedUrl(logo)
      .then((url) => {
        if (active && url) setLogoPreviewUrl(url);
      })
      .catch(() => {
        // Keep the current preview, especially right after cropping/uploading.
      });

    return () => {
      active = false;
    };
  }, [form.logo, form.logoUrl, getFileSignedUrl]);

  useEffect(() => {
    let active = true;
    const favicon = String(form.favicon || "").trim();
    const faviconUrl = String(form.faviconUrl || "").trim();
    setFaviconStatus("");

    if (!favicon) {
      setFaviconPreviewUrl("");
      return () => {
        active = false;
      };
    }

    if (isUrl(faviconUrl)) {
      setFaviconPreviewUrl(faviconUrl);
      return () => {
        active = false;
      };
    }

    if (isUrl(favicon)) {
      setFaviconPreviewUrl(favicon);
      return () => {
        active = false;
      };
    }

    getFileSignedUrl(favicon)
      .then((url) => {
        if (active && url) setFaviconPreviewUrl(url);
      })
      .catch(() => {
        // Keep the current preview, especially right after cropping/uploading.
      });

    return () => {
      active = false;
    };
  }, [form.favicon, form.faviconUrl, getFileSignedUrl]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "logo" ? { logoUrl: "" } : {}),
      ...(field === "favicon" ? { faviconUrl: "" } : {}),
    }));
    setMessage("");
    setLogoStatus("");
    setFaviconStatus("");
  };

  const uploadLogoFile = async (file) => {
    setError("");
    setMessage("");
    setLogoStatus("Uploading logo...");
    const localPreview = URL.createObjectURL(file);
    setLogoPreviewUrl(localPreview);
    try {
      const upload = await uploadFile(actorId || null, file, {
        confirmUpload: true,
        fileType: "PHOTO",
        preserveOriginal: true,
        purpose: "OTHER",
      });
      const fileId = upload?.fileId || upload?.file?.id || "";
      if (!fileId) throw new Error("Upload failed: missing file ID.");
      updateField("logo", fileId);
      const signedUrl = await getFileSignedUrl(fileId).catch(() => "");
      setLogoPreviewUrl(signedUrl || localPreview);
      setLogoStatus("Logo uploaded.");
    } catch (nextError) {
      setLogoStatus("");
      setLogoPreviewUrl("");
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to upload logo.");
    }
  };

  const handleLogoUpload = (event) => {
    const file = Array.from(event.target.files || [])[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError("Choose an image file for the logo.");
      return;
    }

    setError("");
    setMessage("");
    setLogoStatus("");
    setLogoCrop({
      fileName: file.name || "logo.png",
      imageSrc: URL.createObjectURL(file),
    });
  };

  const cancelLogoCrop = () => {
    if (logoCrop?.imageSrc) URL.revokeObjectURL(logoCrop.imageSrc);
    setLogoCrop(null);
  };

  const confirmLogoCrop = async (croppedFile) => {
    const sourceUrl = logoCrop?.imageSrc;
    setLogoCrop(null);
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    await uploadLogoFile(croppedFile);
  };

  const clearLogo = () => {
    updateField("logo", "");
    setLogoPreviewUrl("");
    setLogoStatus("");
  };

  const uploadFaviconFile = async (file) => {
    setError("");
    setMessage("");
    setFaviconStatus("Uploading favicon...");
    const localPreview = URL.createObjectURL(file);
    setFaviconPreviewUrl(localPreview);
    try {
      const upload = await uploadFile(actorId || null, file, {
        confirmUpload: true,
        fileType: "PHOTO",
        preserveOriginal: true,
        purpose: "OTHER",
      });
      const fileId = upload?.fileId || upload?.file?.id || "";
      if (!fileId) throw new Error("Upload failed: missing file ID.");
      updateField("favicon", fileId);
      const signedUrl = await getFileSignedUrl(fileId).catch(() => "");
      setFaviconPreviewUrl(signedUrl || localPreview);
      setFaviconStatus("Favicon uploaded.");
    } catch (nextError) {
      setFaviconStatus("");
      setFaviconPreviewUrl("");
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to upload favicon.");
    }
  };

  const handleFaviconUpload = (event) => {
    const file = Array.from(event.target.files || [])[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError("Choose an image file for the favicon.");
      return;
    }

    setError("");
    setMessage("");
    setFaviconStatus("");
    setFaviconCrop({
      fileName: file.name || "favicon.png",
      imageSrc: URL.createObjectURL(file),
    });
  };

  const cancelFaviconCrop = () => {
    if (faviconCrop?.imageSrc) URL.revokeObjectURL(faviconCrop.imageSrc);
    setFaviconCrop(null);
  };

  const confirmFaviconCrop = async (croppedFile) => {
    const sourceUrl = faviconCrop?.imageSrc;
    setFaviconCrop(null);
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    await uploadFaviconFile(croppedFile);
  };

  const clearFavicon = () => {
    updateField("favicon", "");
    setFaviconPreviewUrl("");
    setFaviconStatus("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedWorkspaceId) {
      setError("Select a workspace before saving site settings.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        ...form,
        workspaceId: selectedWorkspaceId,
        customerInquiryEmails: textToList(inquiryEmails),
        customerInquiryPhones: textToList(inquiryPhones),
      };
      const savedProfile = normalizeProfile(
        await careService.siteProfile.save(payload, {
          actorId,
          wid: selectedWorkspaceId,
          workspaceId: selectedWorkspaceId,
        }),
      );
      setForm(savedProfile);
      setInquiryEmails(listToText(savedProfile.customerInquiryEmails));
      setInquiryPhones(listToText(savedProfile.customerInquiryPhones));
      refreshSiteProfile();
      setMessage("Site settings saved.");
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to save site settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CareShell className="dashboard-content vehicles-page site-settings-page">
      <section className="reports-page-head site-settings-head">
        <div className="page-title">
          <span className="section-eyebrow">{workspaceName}</span>
          <h1>Settings</h1>
        </div>
        <button className="btn primary" type="submit" form="site-settings-form" disabled={saving || loading}>
          <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save settings"}
        </button>
      </section>

      {error ? <p className="form-error">{error}</p> : null}
      {message ? <p className="vehicle-capture-saved">{message}</p> : null}

      {loading ? (
        <div className="site-settings-loading page-inline-loader" role="status" aria-live="polite" aria-label="Loading settings">
          <i className="ti ti-loader-2" aria-hidden="true" />
        </div>
      ) : (
      <form id="site-settings-form" className="site-settings-layout" onSubmit={handleSubmit}>
        <section className="vehicle-capture-card site-settings-card">
          <div className="vehicle-capture-card-head">
            <i className="ti ti-building-store" />
            <div>
              <h2>Site profile</h2>
            </div>
          </div>
          <div className="vehicle-capture-grid">
            <label className="vehicle-capture-field">
              <span>Name</span>
              <input value={form.name || ""} onChange={(event) => updateField("name", event.target.value)} disabled={loading} />
            </label>
            <label className="vehicle-capture-field">
              <span>Theme phrase</span>
              <input value={form.themePhrase || ""} onChange={(event) => updateField("themePhrase", event.target.value)} disabled={loading} />
            </label>
            <label className="vehicle-capture-field">
              <span>Phone</span>
              <input value={form.phone || ""} onChange={(event) => updateField("phone", event.target.value)} disabled={loading} />
            </label>
            <label className="vehicle-capture-field">
              <span>Email</span>
              <input type="email" value={form.email || ""} onChange={(event) => updateField("email", event.target.value)} disabled={loading} />
            </label>
            <label className="vehicle-capture-field full-span">
              <span>Location</span>
              <div className="site-settings-location-field">
                {appConfigLoading && !googleMapsApiKey ? (
                  <input value={form.location || ""} disabled placeholder="Loading address search..." readOnly />
                ) : (
                  <GooglePlacesAutocompleteInput
                    googleMapsApiKey={googleMapsApiKey}
                    placeholder={googleMapsApiKey ? "Search full address" : "Enter full address"}
                    types={["address"]}
                    value={form.location || ""}
                    onChange={(value) => updateField("location", value)}
                    onResolvePlace={async ({ details, prediction }) => {
                      updateField("location", fullAddressFromPlace(details, prediction?.description || form.location || ""));
                    }}
                    disabled={loading}
                  />
                )}
              </div>
            </label>
            <label className="vehicle-capture-field full-span">
              <span>Logo</span>
              <div className="site-settings-logo-uploader">
                <div className="site-settings-logo-preview">
                  {logoPreviewUrl ? (
                    <img src={logoPreviewUrl} alt="Dealership logo preview" />
                  ) : (
                    <i className="ti ti-photo" />
                  )}
                </div>
                <div className="site-settings-logo-controls">
                  <div className="site-settings-logo-actions">
                    <label className="mini-btn" htmlFor="site-logo-upload">
                      <i className="ti ti-upload" /> Upload logo
                    </label>
                    <input
                      accept="image/*"
                      disabled={loading || fileUploading}
                      id="site-logo-upload"
                      onChange={handleLogoUpload}
                      type="file"
                    />
                    {form.logo ? (
                      <button className="mini-btn" disabled={loading || fileUploading} onClick={clearLogo} type="button">
                        <i className="ti ti-trash" /> Remove
                      </button>
                    ) : null}
                  </div>
                  {logoStatus ? <small>{logoStatus}</small> : null}
                </div>
              </div>
            </label>
            <label className="vehicle-capture-field full-span">
              <span>Favicon</span>
              <div className="site-settings-logo-uploader site-settings-favicon-uploader">
                <div className="site-settings-logo-preview site-settings-favicon-preview">
                  {faviconPreviewUrl ? (
                    <img src={faviconPreviewUrl} alt="Favicon preview" />
                  ) : (
                    <i className="ti ti-photo" />
                  )}
                </div>
                <div className="site-settings-logo-controls">
                  <div className="site-settings-logo-actions">
                    <label className="mini-btn" htmlFor="site-favicon-upload">
                      <i className="ti ti-upload" /> Upload favicon
                    </label>
                    <input
                      accept="image/*"
                      disabled={loading || fileUploading}
                      id="site-favicon-upload"
                      onChange={handleFaviconUpload}
                      type="file"
                    />
                    {form.favicon ? (
                      <button className="mini-btn" disabled={loading || fileUploading} onClick={clearFavicon} type="button">
                        <i className="ti ti-trash" /> Remove
                      </button>
                    ) : null}
                  </div>
                  {faviconStatus ? <small>{faviconStatus}</small> : null}
                </div>
              </div>
            </label>
          </div>
        </section>

        <section className="vehicle-capture-card site-settings-card">
          <div className="vehicle-capture-card-head">
            <i className="ti ti-message-circle" />
            <div>
              <h2>Customer inquiries</h2>
            </div>
          </div>
          <div className="vehicle-capture-grid">
            <label className="vehicle-capture-field">
              <span>Customer inquiry emails</span>
              <input value={inquiryEmails} onChange={(event) => setInquiryEmails(event.target.value)} disabled={loading} />
            </label>
            <label className="vehicle-capture-field">
              <span>Customer inquiry phones</span>
              <input value={inquiryPhones} onChange={(event) => setInquiryPhones(event.target.value)} disabled={loading} />
            </label>
          </div>
        </section>

        <section className="vehicle-capture-card site-settings-card">
          <div className="vehicle-capture-card-head">
            <i className="ti ti-palette" />
            <div>
              <h2>Colors</h2>
            </div>
          </div>
          <div className="site-settings-color-grid">
            {[
              ["mainColor", "Main color"],
              ["secondaryColor", "Secondary color"],
              ["siteThemeColor", "Site theme color"],
              ["secondarySiteThemeColor", "Secondary site theme color"],
            ].map(([field, label]) => (
              <label className="site-settings-color-field" key={field}>
                <span>{label}</span>
                <div>
                  <input type="color" value={colorInputValue(form[field])} onChange={(event) => updateField(field, event.target.value)} disabled={loading} />
                  <input value={form[field] || ""} onChange={(event) => updateField(field, event.target.value)} disabled={loading} />
                </div>
              </label>
            ))}
          </div>
        </section>
      </form>
      )}
      {logoCrop ? (
        <SiteImageCropModal
          aspect={3 / 1}
          cropAreaLabel="logo"
          fileName={logoCrop.fileName}
          imageSrc={logoCrop.imageSrc}
          outputType="NAVBAR_LOGO"
          onCancel={cancelLogoCrop}
          onConfirm={confirmLogoCrop}
        />
      ) : null}
      {faviconCrop ? (
        <SiteImageCropModal
          aspect={1}
          cropAreaLabel="favicon"
          fileName={faviconCrop.fileName}
          imageSrc={faviconCrop.imageSrc}
          outputType="LOGO"
          onCancel={cancelFaviconCrop}
          onConfirm={confirmFaviconCrop}
        />
      ) : null}
    </CareShell>
  );
}
